import { AdminPedidoResumen } from "@/pedidos/AdminPedidoResumen";
import { GaleriaBig } from "@/galeria/Galeria";
import { bindProps, formatMoney } from "@/helpers/helpers";
import { useAdminContext } from "@/state/adminContext";
import { POST, queryClient, usePedido } from "@/state/server";
import { type AdminPedido } from "@/state/types";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState, type ReactNode } from "react";
import { z } from "zod";
import { PedidoIcons } from "@/pedidos/PedidoIcons";

export const Route = createFileRoute("/admin/pedido/")({
  component: AdminPedidoPageWrapper,
  validateSearch: z.object({ id: z.number() }),
});

function AdminPedidoPageWrapper() {
  const { id } = Route.useSearch();
  const { data } = usePedido(id);

  return data ? <AdminPedidoPage pedido={data.pedido} /> : "Loading...";
}

function AdminPedidoPage({ pedido }: { pedido: AdminPedido }) {
  const icons = pedido.pagado
    ? () => <></>
    : bindProps(PedidoIcons, { pedido: pedido.id, pref: "/admin" });

  return (
    <GaleriaBig
      fotos={pedido.fotos.filter((f) => f.cantidad && f.cantidad > 0)}
      icons={icons}
      container={({ children }: { children: ReactNode }) => (
        <PedidoContainer pedido={pedido}>{children}</PedidoContainer>
      )}
    ></GaleriaBig>
  );
}

function PedidoContainer({
  pedido,
  children,
}: {
  pedido: AdminPedido;
  children: ReactNode;
}) {
  const admin = useAdminContext();

  const productos = useMemo(
    () => Object.fromEntries(admin.productos_extra.map((p) => [p.id, p])),
    admin.productos_extra
  );

  return (
    <>
      <div className="container">
        <div className="row">
          <div className="col-md-8 col-lg-9">
            {children}
            <hr />
            <ul>
              {pedido.productos
                .filter((p) => p.cantidad > 0)
                .map((p) => (
                  <li key={p.producto}>
                    {pedido.pagado ? (
                      p.cantidad
                    ) : (
                      <input
                        type="number"
                        min={0}
                        style={{ width: 50 }}
                        value={p.cantidad}
                        onChange={(e) =>
                          set_producto_extra(
                            pedido,
                            p.producto,
                            Number(e.target.value)
                          )
                        }
                      />
                    )}{" "}
                    {`${productos[p.producto].descripcion}: ${formatMoney(productos[p.producto].precio * p.cantidad)}`}
                  </li>
                ))}
              {!pedido.pagado && (
                <li>
                  <AddProductoExtra pedido={pedido} />
                </li>
              )}
            </ul>
          </div>
          <div className="col-md-4 col-lg-3">
            <div className="detalles_carrito">
              <AdminPedidoResumen pedido={pedido} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function AddProductoExtra({ pedido }: { pedido: AdminPedido }) {
  const { productos_extra } = useAdminContext();

  const [producto, setProducto] = useState(0);
  const [cantidad, setCantidad] = useState(1);

  return (
    <>
      <select
        value={producto}
        onChange={(e) => setProducto(Number(e.target.value))}
      >
        <option />
        {productos_extra
          .filter(
            (p) =>
              !pedido.productos.find(
                (x) => x.producto == p.id && x.cantidad > 0
              )
          )
          .map((p) => (
            <option
              key={p.id}
              value={p.id}
            >{`${p.descripcion} (${formatMoney(p.precio)})`}</option>
          ))}
      </select>
      <input
        type="number"
        value={cantidad}
        onChange={(e) => setCantidad(Number(e.target.value))}
        min={1}
        style={{ width: 50 }}
      />
      <button onClick={() => set_producto_extra(pedido, producto, cantidad)}>
        AGREGAR
      </button>
    </>
  );
}

function set_producto_extra(
  pedido: AdminPedido,
  producto_id: number,
  cantidad: number
) {
  POST("/admin/pedido/set_producto_extra", {
    pedido_id: pedido.id,
    producto_id,
    cantidad,
  }).then(() =>
    // TODO
    queryClient.invalidateQueries({
      queryKey: [`/admin/pedido/${pedido.id}`],
    })
  );
}
